/**
 * Canonical GreenCharge charger marker (user-app port).
 *
 * The Admin app and the marketing site both render chargers with a
 * gradient bolt-in-circle marker (green = available, blue = in-use,
 * amber = warn, red = error, slate = offline/unknown) with a soft
 * halo on available stations and a highlighted ring on the selected
 * one. Both surfaces build the pin as imperative DOM against
 * `AdvancedMarkerElement`; the user app uses `@react-google-maps/api`
 * whose `<MarkerF>` accepts an SVG data URL instead.
 *
 * Rather than mount a second map binding just to share DOM, this
 * module produces a byte-identical visual as an inline SVG that
 * gets URL-encoded into a data URL. Colours + gradient stops match
 * the Admin's `.gc-marker-*` classes exactly (see
 * sw-ev-admin/.../ChargerNetworkMap.less).
 *
 * ── Resolution priority (highest → lowest) ─────────────────────
 *
 *   1. Under Maintenance     → 'warn'  (amber)
 *   2. Faulted               → 'error' (red)
 *   3. In Use / Charging     → 'busy'  (blue)
 *   4. Unavailable / Offline → 'off'   (slate)
 *   5. Available             → 'avail' (green, with halo)
 *   6. anything else         → 'off'   (slate)
 *
 * Priority is deliberately explicit — an admin-initiated maintenance
 * override MUST always win over the OCPP-derived status, otherwise
 * a customer could see a "Ready to charge" pin on a charger the
 * operator has taken offline. Similarly Faulted beats the ordinary
 * "in use / unavailable" buckets so a truly broken charger reads
 * as such at map zoom-out.
 *
 * Two resolvers, one palette:
 *
 *   markerStatusClass(availability)    → enum-only mapping, no
 *     awareness of maintenance override or raw OCPP fault. Kept
 *     for callers that only have the classified AVAILABILITY value
 *     (existing tests continue to pass unchanged).
 *
 *   resolveMarkerStatusClass(station)  → station-shape resolver
 *     applying the full priority above. This is the API the map
 *     component should use; it consults raw `connector_status` and
 *     the maintenance projection alongside the classified enum, so
 *     a Faulted charger doesn't collapse into a generic slate pin.
 */

import { AVAILABILITY } from './stationAvailability.js';

// GreenCharge status palette. Mirrors the Admin app's LESS constants:
//   @gc-map-green:      #16a34a
//   @gc-map-green-soft: #22c55e
//   @gc-map-blue:       #0ea5e9
//   @gc-map-blue-soft:  #38bdf8
//   @gc-map-amber:      #f59e0b
//   @gc-map-amber-soft: #fbbf24
//   @gc-map-red:        #dc2626
//   @gc-map-red-soft:   #ef4444
//   @gc-map-slate:      #64748b
//   @gc-map-slate-soft: #94a3b8
// Kept as data so tests can assert the mapping without parsing SVG.
export const MARKER_PALETTE = Object.freeze({
  avail: { top: '#16a34a', bottom: '#22c55e', halo: true },
  busy:  { top: '#0ea5e9', bottom: '#38bdf8', halo: false },
  warn:  { top: '#f59e0b', bottom: '#fbbf24', halo: false },
  error: { top: '#dc2626', bottom: '#ef4444', halo: false },
  off:   { top: '#64748b', bottom: '#94a3b8', halo: false },
});

// Public constants for the resolution priority so tests + docs can
// reference the same ordered list rather than re-declaring one.
export const MARKER_STATUS = Object.freeze({
  MAINTENANCE: 'warn',
  FAULTED:     'error',
  IN_USE:      'busy',
  UNAVAILABLE: 'off',
  AVAILABLE:   'avail',
  UNKNOWN:     'off',
});

export const MARKER_PRIORITY = Object.freeze([
  MARKER_STATUS.MAINTENANCE,
  MARKER_STATUS.FAULTED,
  MARKER_STATUS.IN_USE,
  MARKER_STATUS.UNAVAILABLE,
  MARKER_STATUS.AVAILABLE,
  MARKER_STATUS.UNKNOWN,
]);

/**
 * Enum-only mapper — deliberately narrow.
 *
 * Consumers that only have an AVAILABILITY value (list card badge,
 * older call sites) use this. Fault detection requires the raw OCPP
 * status which this signature doesn't accept — use
 * `resolveMarkerStatusClass(station)` for the full priority.
 */
export function markerStatusClass(availability) {
  switch (availability) {
    case AVAILABILITY.AVAILABLE:   return MARKER_STATUS.AVAILABLE;
    case AVAILABILITY.IN_USE:      return MARKER_STATUS.IN_USE;
    case AVAILABILITY.MAINTENANCE: return MARKER_STATUS.MAINTENANCE;
    case AVAILABILITY.UNAVAILABLE: return MARKER_STATUS.UNAVAILABLE;
    case AVAILABILITY.UNKNOWN:     return MARKER_STATUS.UNKNOWN;
    default:                       return MARKER_STATUS.UNKNOWN;
  }
}

// Raw OCPP connector-status values that count as a hard fault for
// marker purposes. Match the user app's `stationAvailability.js`
// UNAVAILABLE set, but split out here because a Faulted charger
// deserves a distinct red pin rather than the generic slate.
const FAULT_CONNECTOR_STATUSES = new Set(['Faulted']);

// Raw OCPP values that read as "offline" — the charger is disabled
// or reporting unavailable, not busy or in a fault state.
const OFFLINE_CONNECTOR_STATUSES = new Set(['Unavailable', 'Disabled']);

/**
 * Station-shape resolver with explicit priority.
 *
 * Accepts anything with the customer-app station shape:
 *   - `availability`     (AVAILABILITY enum from stationAvailability.js)
 *   - `maintenanceMode`  (boolean; also tolerates snake `maintenance_mode`)
 *   - `connectorStatus`  (raw OCPP string; also tolerates snake
 *                         `connector_status`)
 *
 * Non-object / null / undefined input → 'off' (the safe default).
 * See MARKER_PRIORITY for the resolution order.
 */
export function resolveMarkerStatusClass(station) {
  if (!station || typeof station !== 'object') return MARKER_STATUS.UNKNOWN;

  // 1. Maintenance override always wins.
  const isMaintenance =
    station.maintenanceMode === true
    || station.maintenance_mode === true
    || station.availability === AVAILABILITY.MAINTENANCE;
  if (isMaintenance) return MARKER_STATUS.MAINTENANCE;

  // 2. Faulted — raw OCPP connector status trumps the classifier's
  //    UNAVAILABLE bucket so a broken charger reads red, not slate.
  const rawStatus = station.connectorStatus ?? station.connector_status ?? null;
  if (rawStatus && FAULT_CONNECTOR_STATUSES.has(rawStatus)) {
    return MARKER_STATUS.FAULTED;
  }

  // 3–5. Fall through to the classified enum. A missing / unknown
  //      enum lands on 'off' via markerStatusClass's default.
  const cls = markerStatusClass(station.availability);

  // 3b. Explicit offline hint from the raw OCPP status when the
  //     classifier hasn't already promoted to UNAVAILABLE.
  if (cls === MARKER_STATUS.UNKNOWN
      && rawStatus
      && OFFLINE_CONNECTOR_STATUSES.has(rawStatus)) {
    return MARKER_STATUS.UNAVAILABLE;
  }

  return cls;
}

/**
 * Build the raw SVG string for a marker.
 *
 * Layers (bottom → top):
 *   1. Soft halo — only on `avail`, gives available chargers the
 *      subtle glow the Admin app pulses on hover.
 *   2. Selection ring — only when `selected`, wider translucent
 *      halo in the marker's own colour.
 *   3. White outer ring (matches the 2px white stroke around the
 *      Admin marker's gradient circle).
 *   4. Coloured gradient body.
 *   5. White bolt path — same geometry as the Admin `.gc-marker-icon`
 *      path in ClusteredAdminMarkers.buildMarkerContent.
 *
 * Exposed so the test suite can render + assert on the SVG without
 * plumbing google.maps.
 */
export function renderMarkerSvg({ statusClass, selected = false } = {}) {
  const palette = MARKER_PALETTE[statusClass] || MARKER_PALETTE.off;
  const size = selected ? 52 : 40;
  const cx = size / 2;
  const cy = size / 2;
  const bodyR = selected ? 15 : 13;
  const ringR = bodyR + 2;                // white outer ring
  const haloR = bodyR + 6;
  const selRingR = bodyR + 7;
  const gradId = `gc-mk-${statusClass}${selected ? '-sel' : ''}`;

  const halo = palette.halo
    ? `<circle cx="${cx}" cy="${cy}" r="${haloR}" fill="${palette.top}" opacity="0.22" />`
    : '';
  const selectionRing = selected
    ? `<circle cx="${cx}" cy="${cy}" r="${selRingR}" fill="${palette.top}" opacity="0.28" />`
    : '';

  // Bolt path — normalised into the marker's local viewport. The
  // Admin marker draws the bolt in a 24×24 space and centres it in
  // a 36×36 marker; we translate + scale into the current size so
  // the icon reads identical at every marker scale.
  const boltScale = (selected ? 20 : 16) / 24;
  const boltTx = cx - 12 * boltScale;
  const boltTy = cy - 12 * boltScale;

  return `<?xml version="1.0" encoding="UTF-8"?>` +
    `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">` +
      `<defs>` +
        `<linearGradient id="${gradId}" x1="0" y1="0" x2="1" y2="1">` +
          `<stop offset="0%" stop-color="${palette.top}"/>` +
          `<stop offset="100%" stop-color="${palette.bottom}"/>` +
        `</linearGradient>` +
      `</defs>` +
      halo +
      selectionRing +
      `<circle cx="${cx}" cy="${cy}" r="${ringR}" fill="#ffffff"/>` +
      `<circle cx="${cx}" cy="${cy}" r="${bodyR}" fill="url(#${gradId})"/>` +
      `<g transform="translate(${boltTx} ${boltTy}) scale(${boltScale})">` +
        `<path d="M13.5 2.2 4.6 13.1a.6.6 0 0 0 .47.99h5.05l-1.4 7.1a.6.6 0 0 0 1.06.48l8.9-10.9a.6.6 0 0 0-.47-.99h-5.05l1.4-7.1a.6.6 0 0 0-1.06-.48Z" fill="#ffffff"/>` +
      `</g>` +
    `</svg>`;
}

/**
 * Wrap the SVG in a URL-encoded data URL. base64 works too but is
 * larger; `encodeURIComponent` keeps the payload legible for tests
 * and diffs.
 */
export function markerDataUrl(svg) {
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

// Small in-module memo — the marker SVG + data URL is deterministic
// per `(statusClass, selected)`, so re-encoding it for every station
// on every render burns cycles for no benefit. We keep the map to a
// tight bounded size (5 classes × 2 selected states = 10 entries max),
// no eviction needed.
const DATA_URL_CACHE = new Map();
function memoisedDataUrl(statusClass, selected) {
  const key = `${statusClass}|${selected ? 'sel' : 'off'}`;
  const cached = DATA_URL_CACHE.get(key);
  if (cached) return cached;
  const url = markerDataUrl(renderMarkerSvg({ statusClass, selected }));
  DATA_URL_CACHE.set(key, url);
  return url;
}

/**
 * Build the full `<MarkerF icon={...} />` config.
 *
 * Requires `google` — pass in `window.google` (or a shim in tests).
 * Anchor lands at the marker's centre because the pin is a symmetric
 * circle (no tail). Google Maps needs `google.maps.Size` / `Point`
 * instances, so we defer construction until the caller can give us
 * a live namespace.
 *
 * Accepts either `station` (preferred — full priority resolution)
 * or `availability` (enum-only fallback for callers that don't
 * carry the raw station shape).
 *
 * Returns `undefined` when Google Maps is not yet ready so
 * `<MarkerF icon={undefined} />` can safely fall back to the
 * default red pin during the SDK-loading gap.
 */
export function buildMarkerIcon(google, opts = {}) {
  if (!google?.maps?.Size || !google?.maps?.Point) return undefined;
  const { station, availability, selected = false } = opts;
  const statusClass = station
    ? resolveMarkerStatusClass(station)
    : markerStatusClass(availability);
  const url = memoisedDataUrl(statusClass, !!selected);
  const size = selected ? 52 : 40;
  return {
    url,
    scaledSize: new google.maps.Size(size, size),
    anchor: new google.maps.Point(size / 2, size / 2),
  };
}
